import { useState, useEffect, SetStateAction, Dispatch, useRef } from 'react';

interface ResponsiveCollapseArgs {
	threshold?: number;
	overThresholdCollapsed: boolean;
	underThresholdCollapsed: boolean;
}

/**
 * @param {boolean} overThresholdCollapsed - Defines the default behaviour when window.innerWidth is above the threshold
 * @param {boolean} underThresholdCollapsed - Defines the default behaviour when window.innerWidth is under the threshold
 * @param {number} threshold - The width threshold at which the collapse state is reset.
 * @returns {[boolean, Dispatch<SetStateAction<boolean>>]} - The collapsed state and a setter for that state.
 */
const useResponsiveCollapse = ({
	threshold = 1024,
	overThresholdCollapsed,
	underThresholdCollapsed
}: ResponsiveCollapseArgs): [boolean, Dispatch<SetStateAction<boolean>>] => {
	const getCollapsedState = (): boolean =>
		window.innerWidth >= threshold ? overThresholdCollapsed : underThresholdCollapsed;
	const [isCollapsed, setCollapsed] = useState<boolean>(getCollapsedState());
	const beforeWidth = useRef<number>(window.innerWidth);

	useEffect(() => {
		const handleResize = (event: Event | null, isInitial?: boolean) => {
			const newCollapseState = getCollapsedState();
			const jumpUpwardsUpdate: boolean = beforeWidth.current < threshold && window.innerWidth >= threshold;
			const jumpDownwardsUpdate: boolean = beforeWidth.current >= threshold && window.innerWidth < threshold;
			const mustUpdate = isInitial || jumpUpwardsUpdate || jumpDownwardsUpdate;
			if (mustUpdate) {
				setCollapsed(newCollapseState);
			}

			beforeWidth.current = window.innerWidth;
		};
		window.addEventListener('resize', handleResize);
		handleResize(null, true);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [threshold, overThresholdCollapsed, underThresholdCollapsed]);

	return [isCollapsed, setCollapsed];
};

export default useResponsiveCollapse;
